import type {
  AssignCompanyPlanBody,
  BulkCompanyActionBody,
  CreateCompanyBody,
} from '@planuze/admin-contracts';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  assignCompanyPlan,
  blockCompany,
  bulkCompanyAction,
  type CompaniesQuery,
  createCompany,
  getCompany,
  listCompanies,
  publisherSlugAvailable,
  unblockCompany,
} from '../lib/companies-api.js';
import { useDebounce } from './use-debounce.js';

/* Hooks TanStack Query do domínio empresas (ADR-0354). */

export type SlugCheckState = 'idle' | 'checking' | 'available' | 'taken';

/* Pré-check debounced do namespace `@slug` (ADR-0600): estados idle/checking/
   available/taken p/ o create-company. Só consulta quando o formato já é válido
   (`enabled`) e o slug não é vazio; a autoridade final segue no provision (409). */
export function usePublisherSlugAvailable(slug: string, enabled: boolean): SlugCheckState {
  /* 400ms inline (mesmo padrão dos pickers org/user via useDebounce): técnico-UI. */
  const debounced = useDebounce(slug, 400);
  const active = enabled && debounced.length > 0;
  const query = useQuery({
    queryKey: ['companies', 'slug-available', debounced],
    queryFn: () => publisherSlugAvailable(debounced),
    enabled: active,
  });
  if (!enabled || slug.length === 0) return 'idle';
  if (debounced !== slug || query.isFetching || query.data === undefined) return 'checking';
  return query.data ? 'available' : 'taken';
}

/* Criação de org admin-created (ADR-0421): invalida a lista (novo item aparece). */
export function useCreateCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateCompanyBody) => createCompany(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companies', 'list'] });
    },
  });
}

/* Atribuir o plano custom da org (C6, ADR-0422): invalida o detalhe (a assinatura/
   plano da org muda). */
export function useAssignCompanyPlan(companyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AssignCompanyPlanBody) => assignCompanyPlan(companyId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companies', 'detail', companyId] });
    },
  });
}

export function useCompaniesList(filters: CompaniesQuery) {
  return useInfiniteQuery({
    queryKey: ['companies', 'list', filters],
    queryFn: ({ pageParam }) => listCompanies({ ...filters, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}

/* `options.enabled` permite adiar a query até o consumidor precisar (ex.: painel de contexto
   do chat que só carrega a org quando a aba fica ativa e o ticket tem `orgId`). Default true —
   retrocompatível com os call sites que não passam options (ADR-0704). */
export function useCompanyDetail(id: string, options?: { readonly enabled?: boolean }) {
  return useQuery({
    queryKey: ['companies', 'detail', id],
    queryFn: () => getCompany(id),
    enabled: options?.enabled ?? true,
  });
}

function useCompanyMutation(
  id: string,
  op: (id: string, reason: string) => ReturnType<typeof blockCompany>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (reason: string) => op(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companies', 'detail', id] });
      queryClient.invalidateQueries({ queryKey: ['companies', 'list'] });
    },
  });
}

export function useBlockCompany(id: string) {
  return useCompanyMutation(id, blockCompany);
}

export function useUnblockCompany(id: string) {
  return useCompanyMutation(id, unblockCompany);
}

export function useBulkCompanyAction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: BulkCompanyActionBody) => bulkCompanyAction(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companies'] });
    },
  });
}
